import { BaseQueryOptions } from '@apollo/client'
import { graphql, ChildProps } from '@apollo/client/react/hoc'
import { RouteComponentProps } from 'react-router-dom'

import { NavigationMenu } from '../../../types/nav'

import { getCountry } from '../../helpers/get-country'
import { ApplicationError, NotFoundError } from '../../helpers/errors'
import { graphqlToFeaturedLink } from '../../helpers/graphql-to-featured-link'
import { graphqlToMarketplaceRetailerReducer } from '../../helpers/graphql-to-marketplace-retailer-reducer'
import { nationalExperiencePageToNavLink } from '../../helpers/national-experience-page-to-nav-link'

import { Variables, Response, Props, NavigationMenuObj } from './types'
import query from './query.graphql'

export { query }

export const mapPropsToOptions = ({
  staticContext,
}: RouteComponentProps): BaseQueryOptions<Variables> => ({
  variables: { country: getCountry(staticContext) },
  errorPolicy: 'ignore',
  context: { version: 2 },
})

export const mapDataToProps = ({
  data,
}: Response): ChildProps<Props, Response, Variables> => {
  if (!data) {
    return { error: new ApplicationError('No data') }
  }
  const {
    error,
    loading,
    cfNationalHomepageCollection,
    cfNationalExperiencePageCollection,
  } = data
  if (error) {
    return {
      error: new ApplicationError(error),
    }
  } else if (loading) {
    return { loading }
  }

  const homepage = cfNationalHomepageCollection?.items[0]

  if (!homepage) {
    return {
      error: new NotFoundError(),
    }
  }

  const {
    navigationMenu,
    promotionModule,
    featuresCollection,
    featuredRetailersCollection,
    featuredCategoriesCollection,
  } = homepage

  const isLegacy = (
    navigationMenu: NavigationMenu | NavigationMenuObj
  ): navigationMenu is NavigationMenu => Array.isArray(navigationMenu)

  return {
    promotion: promotionModule,
    features: featuresCollection?.items.map(graphqlToFeaturedLink),
    retailers: featuredRetailersCollection?.items.reduce(
      graphqlToMarketplaceRetailerReducer,
      []
    ),
    featuredCategoriesCollection: featuredCategoriesCollection?.items.map(
      graphqlToFeaturedLink
    ),
    nationalNavLinks: {
      navigationMenu: isLegacy(navigationMenu)
        ? navigationMenu
        : navigationMenu?.nav,
    },
    nationalExperienceNavLinks: isLegacy(navigationMenu)
      ? cfNationalExperiencePageCollection?.items.map(
          nationalExperiencePageToNavLink
        )
      : navigationMenu?.featured,
  }
}

const withNationalHomepage = graphql(query, {
  options: mapPropsToOptions,
  props: mapDataToProps,
})

export default withNationalHomepage
